import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { DatePicker } from '@components/core-ui/form-elements/DatePicker';
import { TJobsFilter } from 'app/types/TJobsFilter';
import { TUserFilter } from 'app/types/TUserFilter';
import { useState } from 'react';

import { getFirstDayOfCurrentMonth } from './helpers/getFirstDayOfCurrentMonth';
import { getFirstDayOfCurrentWeek } from './helpers/getFirstDayOfCurrentWeek';
import { getSelectedDateFilterValue } from './helpers/getSelectedDateFilterValue';
import { getStartOfCurrentDay } from './helpers/getStartOfCurrentDay';
import { TProps } from './types/TProps';

export const ReportFilter = ({
  userFilter,
  userFilterOnChange,
  jobsFilter,
  jobsFilterOnChange,
  handleSubmit,
  handleClose,
}: TProps) => {
  const [tempDateFilterSelected, setTempDateFilterSelected] = useState(getSelectedDateFilterValue(jobsFilter));
  const [tempJobsFilter, setTempJobsFilter] = useState(jobsFilter);
  const [tempUserFilter, setTempUserFilter] = useState(userFilter);

  const tempSubmit = () => {
    if (userFilterOnChange) {
      userFilterOnChange(tempUserFilter ?? {});
    }
    if (jobsFilterOnChange) {
      jobsFilterOnChange(tempJobsFilter);
    }
    handleSubmit();
    handleClose();
  };

  return (
    <div className="flex cursor-default flex-col">
      <p className="m-0 pb-[15px] text-xs font-medium text-[var(--color-text-muted)]">Jobs submitted</p>
      <div className="pb-3">
        <DatePicker
          label="From"
          minDate={new Date(0)}
          maxDate={tempJobsFilter.to || new Date()}
          value={tempJobsFilter.from}
          onChange={(date: Date | null) =>
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = date ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <div className="pb-3">
        <DatePicker
          label="To"
          minDate={tempJobsFilter.from}
          maxDate={new Date()}
          value={tempJobsFilter.to}
          onChange={(date: Date | null) =>
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              let newDate = null;
              if (date) {
                const dateObj = new Date(date);
                dateObj.setHours(23);
                dateObj.setMinutes(59);
                newDate = dateObj;
              }
              newVal.to = newDate ?? undefined;
              return newVal;
            })
          }
        />
      </div>

      <div className="flex justify-between gap-[5px]">
        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'today'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = getStartOfCurrentDay();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('today');
          }}
        >
          Today
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'week'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = getFirstDayOfCurrentWeek();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('week');
          }}
        >
          This week
        </OutlinedButton>

        <OutlinedButton
          className="w-full"
          disabled={tempDateFilterSelected === 'month'}
          onClick={() => {
            setTempJobsFilter((v: TJobsFilter) => {
              const newVal = Object.assign({}, v);
              newVal.from = getFirstDayOfCurrentMonth();
              newVal.to = new Date();
              return newVal;
            });
            setTempDateFilterSelected('month');
          }}
        >
          This month
        </OutlinedButton>
      </div>

      {userFilter && userFilterOnChange ? (
        <div className="mt-5">
          <p className="m-0 pb-[15px] text-xs font-medium text-[var(--color-text-muted)]">Users created</p>
          <div className="pb-3">
            <DatePicker
              minDate={new Date(0)}
              maxDate={tempUserFilter?.userTo || new Date()}
              value={tempUserFilter?.userFrom}
              onChange={(date: Date | null) =>
                setTempUserFilter((v) => {
                  const newVal = Object.assign({}, v);
                  newVal.userFrom = date;
                  return newVal;
                })
              }
              label="From"
            />
          </div>
          <div className="pb-3">
            <DatePicker
              minDate={tempUserFilter?.userFrom ?? undefined}
              maxDate={new Date()}
              value={tempUserFilter?.userTo}
              onChange={(date: Date | null) =>
                setTempUserFilter((v) => {
                  const newVal: TUserFilter = Object.assign({}, v);
                  let newDate = null;
                  if (date) {
                    const dateObj = new Date(date);
                    dateObj.setHours(23);
                    dateObj.setMinutes(59);
                    newDate = dateObj;
                  }
                  newVal.userTo = newDate;
                  return newVal;
                })
              }
              label="To"
            />
          </div>
        </div>
      ) : null}

      <div className="mt-5 flex gap-2.5 [&_button]:m-0 [&_button]:flex-[1_1_auto]">
        <OutlinedButton
          onClick={() => {
            if (userFilterOnChange) {
              setTempUserFilter((v) => {
                const newVal = Object.assign({}, v);
                newVal.userFrom = undefined;
                newVal.userTo = undefined;
                return newVal;
              });
            }
            setTempJobsFilter((v) => {
              const newVal = Object.assign({}, v);
              newVal.from = undefined;
              newVal.to = undefined;
              return newVal;
            });
            setTempDateFilterSelected('');
            if (userFilterOnChange) {
              userFilterOnChange((v: TUserFilter) => {
                const newVal = Object.assign({}, v);
                newVal.userFrom = undefined;
                newVal.userTo = undefined;
                return newVal;
              });
            }
            if (jobsFilterOnChange) {
              jobsFilterOnChange((v: TJobsFilter) => {
                const newVal = Object.assign({}, v);
                newVal.from = undefined;
                newVal.to = undefined;
                return newVal;
              });
            }
            handleSubmit();
            handleClose();
          }}
        >
          Reset
        </OutlinedButton>
        <Button onClick={tempSubmit}>Apply</Button>
      </div>
    </div>
  );
};
